import type { HttpError } from '@refinedev/core'
import { useInvalidate, useList, useNotification } from '@refinedev/core'
import type { AdminInputs, GameConfiguration } from '@sirius/contracts/admin'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { Control } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAdminClient } from '~/api/adminClientContext'
import { InheritableSelect } from '~/components/inheritableSelect'
import { Button } from '~/components/ui/button'
import { Form, FormField, FormItem, FormLabel } from '~/components/ui/form'
import { configurationOptionLabel } from '~/pages/cabinets/configurations'
import { useGameOptions } from '~/pages/cabinets/useGameOptions'
import { TENANT_CONFIGURATIONS_RESOURCE } from '~/providers/dataProvider'
import { errorMessage } from '~/providers/errors'
import { useActiveTenant } from '~/tenancy/activeTenant'
import {
  changedDefaults,
  choicesOf,
  configurationName,
  defaultConfigurationsQueryKey,
  useTenantDefaultConfigurations,
} from './defaultConfigurations'

/**
 * Each game's select value (`USE_DEFAULT` or a configuration id), keyed by
 * game id. Not Refine's `useForm`: no record key can overwrite it.
 */
type ConfigurationsValues = { configurations: Record<string, string> }

/**
 * One game's default configuration: "Use default (<the game's default>)",
 * then the configurations the host tenant may run (the platform's and its
 * own, `tenantConfigurations`).
 */
function DefaultConfigurationField({
  control,
  gameId,
  gameName,
  inheritedConfigurationId,
  disabled,
}: {
  control: Control<ConfigurationsValues>
  gameId: string
  gameName: string
  inheritedConfigurationId: string | null
  disabled: boolean
}) {
  const { t } = useTranslation()
  const { result, query } = useList<GameConfiguration, HttpError>({
    resource: TENANT_CONFIGURATIONS_RESOURCE,
    filters: [{ field: 'gameId', operator: 'eq', value: gameId }],
    sorters: [{ field: 'name', order: 'asc' }],
    pagination: { mode: 'off' },
  })
  const configurations = query.isSuccess ? result.data : undefined

  return (
    <FormField
      control={control}
      name={`configurations.${gameId}`}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{gameName}</FormLabel>
          <InheritableSelect
            aria-label={t('settingsPage.configurations.selectLabel', {
              game: gameName,
            })}
            value={field.value ?? ''}
            disabled={disabled}
            onChange={field.onChange}
            options={(configurations ?? []).map((configuration) => ({
              value: configuration.id,
              label: configurationOptionLabel(configuration, t),
            }))}
            inheritedLabel={configurationName(
              configurations,
              inheritedConfigurationId,
            )}
          />
          {query.isError && (
            <p className="text-destructive text-sm">
              {t('settingsPage.configurations.gameLoadError', {
                game: gameName,
              })}
            </p>
          )}
        </FormItem>
      )}
    />
  )
}

/**
 * The host tenant's default configuration of each game a cabinet can offer:
 * what its cabinets without a configuration of their own start on. Staff
 * and the tenant's owners/admins (`DEFAULT_CONFIGURATIONS_RESOURCE`) set
 * them; one Save sends the games that changed.
 */
export function ConfigurationsTab() {
  const { t } = useTranslation()
  const tenant = useActiveTenant()
  const client = useAdminClient()
  const queryClient = useQueryClient()
  const invalidate = useInvalidate()
  const { open } = useNotification()
  const { names } = useGameOptions()
  const defaults = useTenantDefaultConfigurations(tenant.id)
  const form = useForm<ConfigurationsValues>({
    defaultValues: { configurations: {} },
  })
  const { reset } = form

  useEffect(() => {
    if (defaults.data !== undefined) {
      reset({ configurations: choicesOf(defaults.data) })
    }
  }, [defaults.data, reset])

  const save = useMutation({
    mutationFn: (
      games: AdminInputs['tenants']['defaultConfigurations']['update']['games'],
    ) =>
      client.tenants.defaultConfigurations.update({
        tenantId: tenant.id,
        games,
      }),
    onSuccess: async (saved) => {
      queryClient.setQueryData(defaultConfigurationsQueryKey(tenant.id), saved)
      open?.({
        type: 'success',
        message: t('settingsPage.configurations.savedNotification'),
      })
      // cabinets on Use default show the configuration they start on
      await invalidate({ resource: 'cabinets', invalidates: ['resourceAll'] })
    },
    onError: (error) => {
      open?.({
        type: 'error',
        message: t('settingsPage.configurations.saveError'),
        description: errorMessage(error),
      })
    },
  })

  if (defaults.isError) {
    return (
      <p
        role="alert"
        className="text-destructive text-sm"
      >
        {t('settingsPage.configurations.loadError', {
          message: errorMessage(defaults.error),
        })}
      </p>
    )
  }

  const pending = defaults.data === undefined || save.isPending

  return (
    <Form {...form}>
      <form
        aria-busy={pending}
        aria-label={t('settingsPage.configurations.formLabel')}
        onSubmit={form.handleSubmit(({ configurations }) => {
          if (defaults.data !== undefined) {
            save.mutate(changedDefaults(defaults.data, configurations))
          }
        })}
        className="max-w-md space-y-4"
      >
        <p className="text-muted-foreground text-sm">
          {t('settingsPage.configurations.intro')}
        </p>
        {defaults.data?.games.map(({ gameId, inheritedConfigurationId }) => (
          <DefaultConfigurationField
            key={gameId}
            control={form.control}
            gameId={gameId}
            gameName={
              names.isLoading
                ? t('settingsPage.configurations.gameLoading')
                : (names.byId.get(gameId) ?? gameId)
            }
            inheritedConfigurationId={inheritedConfigurationId}
            disabled={pending}
          />
        ))}
        <Button
          type="submit"
          disabled={pending}
        >
          {t('settingsPage.configurations.save')}
        </Button>
      </form>
    </Form>
  )
}
